// =============================================================================
// ENABLE 2FA MODAL COMPONENT
// =============================================================================
// Ubicación: src/components/TwoFactor/Enable2FAModal.tsx
// =============================================================================

import React, { useState, useEffect } from 'react';
import { Modal, Steps, Button, Input, Typography, Space, Alert, Spin, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { QRCodeSVG } from 'qrcode.react';
import { CopyOutlined, DownloadOutlined, SafetyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { enable2FA, verify2FASetup } from '../../services/AuthenticationService';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface Enable2FAModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const Enable2FAModal: React.FC<Enable2FAModalProps> = ({ visible, onClose, onSuccess }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');

  // ==========================================================================
  // STEP 1: Initialize 2FA (get QR code and backup codes)
  // ==========================================================================
  useEffect(() => {
    if (visible) {
      initiate2FA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const initiate2FA = async () => {
    setLoading(true);
    try {
      const response = await enable2FA();
      setQrCodeUrl(response.qrCodeUrl);
      setManualEntryKey(response.manualEntryKey);
      setBackupCodes(response.backupCodes);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.ENABLE_2FA_INITIATE_ERROR));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // STEP 2: Verify setup with TOTP code
  // ==========================================================================
  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      message.warning(translate(t, TRANSLATIONS.ENABLE_2FA_CODE_WARNING));
      return;
    }

    setLoading(true);
    try {
      await verify2FASetup(verificationCode);
      message.success(translate(t, TRANSLATIONS.ENABLE_2FA_ENABLED_SUCCESS));
      setCurrentStep(2); // Move to backup codes step
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.ENABLE_2FA_INVALID_CODE_ERROR));
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // HELPER FUNCTIONS
  // ==========================================================================
  const copyManualKey = () => {
    navigator.clipboard.writeText(manualEntryKey);
    message.success(translate(t, TRANSLATIONS.ENABLE_2FA_KEY_COPIED));
  };

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '2fa-backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success(translate(t, TRANSLATIONS.ENABLE_2FA_CODES_DOWNLOADED));
  };

  const copyBackupCodes = () => {
    const content = backupCodes.join('\n');
    navigator.clipboard.writeText(content);
    message.success(translate(t, TRANSLATIONS.ENABLE_2FA_CODES_COPIED));
  };

  const handleClose = () => {
    if (currentStep === 2) {
      // Successfully completed - call onSuccess
      onSuccess();
    }
    // Reset state
    setCurrentStep(0);
    setVerificationCode('');
    onClose();
  };

  // ==========================================================================
  // RENDER STEPS
  // ==========================================================================
  const renderStep = () => {
    switch (currentStep) {
      case 0: // Scan QR Code
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message={translate(t, TRANSLATIONS.ENABLE_2FA_STEP1_TITLE)}
              description={translate(t, TRANSLATIONS.ENABLE_2FA_STEP1_DESCRIPTION)}
              type="info"
              showIcon
            />

            <div style={{ textAlign: 'center' }}>
              {loading ? (
                <Spin size="large" />
              ) : (
                <QRCodeSVG value={qrCodeUrl} size={200} level="H" />
              )}
            </div>

            <div>
              <Text strong>{translate(t, TRANSLATIONS.ENABLE_2FA_MANUAL_ENTRY_LABEL)}</Text>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Input
                  value={manualEntryKey}
                  readOnly
                  style={{ fontFamily: 'monospace', borderRadius: 16 }}
                />
                <Button shape="round" icon={<CopyOutlined />} onClick={copyManualKey}>
                  {translate(t, TRANSLATIONS.ENABLE_2FA_COPY_BUTTON)}
                </Button>
              </div>
            </div>

            <Button
              type="primary"
              shape="round"
              block
              onClick={() => setCurrentStep(1)}
              disabled={loading}
            >
              {translate(t, TRANSLATIONS.ENABLE_2FA_NEXT_VERIFY_BUTTON)}
            </Button>
          </Space>
        );

      case 1: // Verify Code
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message={translate(t, TRANSLATIONS.ENABLE_2FA_STEP2_TITLE)}
              description={translate(t, TRANSLATIONS.ENABLE_2FA_STEP2_DESCRIPTION)}
              type="info"
              showIcon
            />

            <div>
              <Text strong>{translate(t, TRANSLATIONS.ENABLE_2FA_ENTER_CODE_LABEL)}</Text>
              <Input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{
                  fontSize: 24,
                  textAlign: 'center',
                  letterSpacing: 8,
                  fontFamily: 'monospace',
                  borderRadius: 16,
                }}
                autoFocus
              />
            </div>

            <Space style={{ width: '100%' }}>
              <Button shape="round" onClick={() => setCurrentStep(0)}>
                {translate(t, TRANSLATIONS.ENABLE_2FA_BACK_BUTTON)}
              </Button>
              <Button
                type="primary"
                shape="round"
                onClick={handleVerify}
                loading={loading}
                disabled={verificationCode.length !== 6}
                style={{ flex: 1 }}
              >
                {translate(t, TRANSLATIONS.ENABLE_2FA_VERIFY_ENABLE_BUTTON)}
              </Button>
            </Space>
          </Space>
        );

      case 2: // Save Backup Codes
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message={translate(t, TRANSLATIONS.ENABLE_2FA_STEP3_TITLE)}
              description={translate(t, TRANSLATIONS.ENABLE_2FA_STEP3_DESCRIPTION)}
              type="success"
              showIcon
            />

            <div>
              <Title level={5}>
                <SafetyOutlined /> {translate(t, TRANSLATIONS.ENABLE_2FA_BACKUP_CODES_TITLE)}
              </Title>
              <Paragraph type="secondary">
                {translate(t, TRANSLATIONS.ENABLE_2FA_BACKUP_CODES_HINT)}
              </Paragraph>

              <div
                style={{
                  background: token.colorFillQuaternary,
                  padding: 16,
                  borderRadius: 14,
                  fontFamily: 'monospace',
                  fontSize: 14
                }}
              >
                {backupCodes.map((code, index) => (
                  <div key={index} style={{ padding: '4px 0' }}>
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <Space style={{ width: '100%' }}>
              <Button
                shape="round"
                icon={<DownloadOutlined />}
                onClick={downloadBackupCodes}
              >
                {translate(t, TRANSLATIONS.ENABLE_2FA_DOWNLOAD_BUTTON)}
              </Button>
              <Button
                shape="round"
                icon={<CopyOutlined />}
                onClick={copyBackupCodes}
              >
                {translate(t, TRANSLATIONS.ENABLE_2FA_COPY_BUTTON)}
              </Button>
            </Space>

            <Alert
              message={translate(t, TRANSLATIONS.ENABLE_2FA_IMPORTANT_ALERT_MESSAGE)}
              description={translate(t, TRANSLATIONS.ENABLE_2FA_IMPORTANT_ALERT_DESCRIPTION)}
              type="warning"
              showIcon
            />

            <Button type="primary" shape="round" block onClick={handleClose}>
              {translate(t, TRANSLATIONS.ENABLE_2FA_DONE_BUTTON)}
            </Button>
          </Space>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <div>
          <SafetyOutlined /> {translate(t, TRANSLATIONS.ENABLE_2FA_MODAL_TITLE)}
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      // Same convention as FiltersModal -- min() keeps 500px on desktop
      // (92vw is always wider there) while giving narrow/tablet widths a
      // safety margin antd's own viewport clamp only provides below ~575px.
      width="min(500px, 92vw)"
      centered
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title={translate(t, TRANSLATIONS.ENABLE_2FA_STEPS_SCAN)} />
        <Step title={translate(t, TRANSLATIONS.ENABLE_2FA_STEPS_VERIFY)} />
        <Step title={translate(t, TRANSLATIONS.ENABLE_2FA_STEPS_SAVE)} />
      </Steps>

      {renderStep()}
    </Modal>
  );
};

export default Enable2FAModal;
